import { useMutation } from "@tanstack/react-query";
import { practiceUsersApi } from "apis";
import { useToast } from "contexts/ToastContext";

interface LearningRedirectResponse {
  success: boolean;
  url?: string;
  message?: string;
}

export interface UseLearningRedirectResult {
  redirectToLearning: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const useLearningRedirect = (): UseLearningRedirectResult => {
  const { showSuccess, showError } = useToast();

  const {
    mutateAsync: redirectToLearning,
    isPending: isLoading,
    error: mutationError,
  } = useMutation({
    mutationFn: async (): Promise<LearningRedirectResponse> => {
      const response = await practiceUsersApi.getLearningRedirect();

      // Type assertion to access the response properties
      const responseData = response as unknown as LearningRedirectResponse;

      return responseData;
    },
    onSuccess: (data) => {
      if (data.success && data.url) {
        // Redirect to the learning platform in a new tab
        window.open(data.url, "_blank", "noopener,noreferrer");
        showSuccess(
          "Redirecting to Learning Platform",
          "Opening the learning platform in a new tab..."
        );
      } else {
        // Show error message from the API response
        showError(
          "Unable to Access Learning Platform",
          data.message || "An error occurred. Please try again."
        );
      }
    },
    onError: (error) => {
      console.error("Failed to get learning redirect:", error);
      showError(
        "Error",
        "Failed to access the learning platform. Please try again later."
      );
    },
  });

  // Transform mutation error to string
  const error = mutationError
    ? mutationError instanceof Error
      ? mutationError.message
      : "Unknown error"
    : null;

  return {
    redirectToLearning: async () => {
      await redirectToLearning();
    },
    isLoading,
    error,
  };
};

export default useLearningRedirect;
